import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Typography,
    useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TrackedTextField } from "src/theme/StyledFields";
import { searchYouTube, YouTubeVideo } from "./youtubeApi";

export type QuerySource = "user" | "ai";

interface YouTubeSearchProps {
    /** AI-generated query for this slide; used as the seed when the user hasn't typed anything. */
    aiQuery?: string | null;
    /** Restores a prior user query (e.g. from the URL dev backdoor). */
    initialQuery?: string;
    selectedVideoId?: string | null;
    onSelectVideo: (video: YouTubeVideo, querySource: QuerySource) => void;
    /** Notifies the parent of the effective query so it can be persisted (dev backdoor). */
    onQueryChange?: (query: string) => void;
    /** Fires once when the first page-1 search completes with results. */
    onInitialLoadComplete?: (video: YouTubeVideo, querySource: QuerySource) => void;
}

const DEBOUNCE_MS = 500;

function formatPublished(publishedAt: string): string {
    if (!publishedAt) return "";
    const date = new Date(publishedAt);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, { year: "numeric", month: "short" });
}

const YouTubeSearch: React.FC<YouTubeSearchProps> = ({
    aiQuery,
    initialQuery,
    selectedVideoId,
    onSelectVideo,
    onQueryChange,
    onInitialLoadComplete,
}) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [inputValue, setInputValue] = useState(initialQuery ?? "");
    const [debouncedInput, setDebouncedInput] = useState(initialQuery ?? "");
    const [results, setResults] = useState<YouTubeVideo[]>([]);
    const [loading, setLoading] = useState(false);
    // Tracks the query the current `results` were fetched for, so we can hold a
    // preloader until the first fetch for the active query settles instead of
    // briefly flashing an empty-state message.
    const [settledQuery, setSettledQuery] = useState<string | null>(null);
    const [pageToken, setPageToken] = useState<string | undefined>(undefined);
    const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);
    const [prevPageToken, setPrevPageToken] = useState<string | undefined>(undefined);

    // The user-typed query always wins; otherwise fall back to the AI-provided query.
    const trimmedInput = debouncedInput.trim();
    const effectiveQuery = trimmedInput || (aiQuery ?? "").trim();
    const querySource: QuerySource = trimmedInput ? "user" : "ai";

    const onQueryChangeRef = useRef(onQueryChange);
    onQueryChangeRef.current = onQueryChange;
    const onInitialLoadCompleteRef = useRef(onInitialLoadComplete);
    onInitialLoadCompleteRef.current = onInitialLoadComplete;
    const initialLoadCompleteRef = useRef(false);
    const enqueueSnackbarRef = useRef(enqueueSnackbar);
    enqueueSnackbarRef.current = enqueueSnackbar;
    const pagedQueryRef = useRef(effectiveQuery);

    // Debounce the text input to limit API calls (quota).
    useEffect(() => {
        const handle = window.setTimeout(() => setDebouncedInput(inputValue), DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [inputValue]);

    useEffect(() => {
        setPageToken(undefined);
    }, [effectiveQuery]);

    useEffect(() => {
        onQueryChangeRef.current?.(effectiveQuery);

        if (!effectiveQuery) {
            setResults([]);
            setNextPageToken(undefined);
            setPrevPageToken(undefined);
            return;
        }

        const pageTokenForRequest =
            pagedQueryRef.current === effectiveQuery ? pageToken : undefined;
        pagedQueryRef.current = effectiveQuery;

        const controller = new AbortController();
        let active = true;
        setLoading(true);

        searchYouTube(effectiveQuery, { pageToken: pageTokenForRequest, signal: controller.signal })
            .then((page) => {
                if (active) {
                    setResults(page.videos);
                    setNextPageToken(page.nextPageToken);
                    setPrevPageToken(page.prevPageToken);
                }
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) return;
                const message = error instanceof Error ? error.message : "YouTube search failed.";
                enqueueSnackbarRef.current(message, { variant: "error" });
                if (active) {
                    setResults([]);
                    setNextPageToken(undefined);
                    setPrevPageToken(undefined);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                    setSettledQuery(effectiveQuery);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [effectiveQuery, pageToken]);

    // Notify parent of the first result once on initial load. Separate effect so a fetch
    // abort/re-run cannot skip the callback after results have already been rendered.
    useEffect(() => {
        if (
            initialLoadCompleteRef.current ||
            loading ||
            pageToken !== undefined ||
            results.length === 0
        ) {
            return;
        }
        initialLoadCompleteRef.current = true;
        onInitialLoadCompleteRef.current?.(results[0], querySource);
    }, [loading, pageToken, results, querySource]);

    const resultCards = useMemo(
        () =>
            results.map((video) => {
                const isSelected = video.videoId === selectedVideoId;
                const published = formatPublished(video.publishedAt);
                return (
                    <Box
                        key={video.videoId}
                        component="button"
                        type="button"
                        onClick={() => onSelectVideo(video, querySource)}
                        data-attr="youtube_select-result"
                        data-custom-id={video.videoId}
                        data-testid="youtube-result-card"
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            textAlign: "left",
                            width: "100%",
                            p: 1,
                            border: `2px solid ${isSelected ? theme.palette.primary.main : "transparent"}`,
                            borderRadius: 1,
                            backgroundColor: isSelected
                                ? theme.palette.action.selected
                                : "transparent",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                    >
                        <Box
                            component="img"
                            src={video.thumbnail}
                            alt=""
                            sx={{ width: 120, height: 68, borderRadius: 1, objectFit: "cover", flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {video.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                {video.channelTitle}
                                {published ? ` \u00b7 ${published}` : ""}
                            </Typography>
                        </Box>
                    </Box>
                );
            }),
        [results, selectedVideoId, querySource, onSelectVideo, theme]
    );

    // A search is pending/in flight while a fetch runs, while the active query
    // still needs its first fetch to settle, or while keystrokes are being
    // debounced into a pending search — this prevents an empty-state flash.
    const isDebouncing = inputValue.trim() !== debouncedInput.trim();
    const searchActive =
        loading ||
        (Boolean(effectiveQuery) && settledQuery !== effectiveQuery) ||
        (isDebouncing && Boolean(inputValue.trim()));

    // Full-list preloader only when there are no results yet to show (initial
    // open). Once results exist, keep them in place and surface a small spinner
    // in the input instead so the list doesn't blank out while reloading.
    const showFullLoader = searchActive && results.length === 0;
    const showInlineLoader = searchActive && results.length > 0;

    return (
        <Box data-testid="youtube-search">
            <TrackedTextField
                fullWidth
                size="small"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Search for a video..."
                data-attr="youtube_search-input"
                inputProps={{ "data-testid": "youtube-search-input" }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: showInlineLoader ? (
                        <InputAdornment position="end">
                            <CircularProgress size={18} />
                        </InputAdornment>
                    ) : undefined,
                }}
            />

            <Box
                data-testid="youtube-results"
                sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 280, overflowY: "auto" }}
            >
                {showFullLoader ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : results.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                        {effectiveQuery ? "No videos found." : "Type a search to find videos."}
                    </Typography>
                ) : (
                    resultCards
                )}
            </Box>

            {!showFullLoader && results.length > 0 && (prevPageToken || nextPageToken) ? (
                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Button
                        size="small"
                        startIcon={<ChevronLeftIcon />}
                        disabled={!prevPageToken}
                        onClick={() => setPageToken(prevPageToken)}
                        data-attr="youtube_results-prev"
                    >
                        Previous
                    </Button>
                    <Button
                        size="small"
                        endIcon={<ChevronRightIcon />}
                        disabled={!nextPageToken}
                        onClick={() => setPageToken(nextPageToken)}
                        data-attr="youtube_results-next"
                    >
                        Next
                    </Button>
                </Box>
            ) : null}
        </Box>
    );
};

export default YouTubeSearch;
